declare global {
  interface Window {
    Turbo?: {
      renderStreamMessage: (html: string) => void;
    };
  }
}

import {
  createGrid,
  GridOptions,
  GridState,
  ColDef,
  CellRange,
  Column,
  IRowNode,
  SelectionChangedEvent,
  StateUpdatedEvent,
  CellSelectionChangedEvent,
} from "ag-grid-enterprise";
import type { SideBarDef, MenuItemDef, DefaultMenuItem } from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { StatusCellRenderer } from "../ag_grid_shared/cell_renderers/status_cell_renderer";
import { ebTheme } from "../ag_grid_shared/eb_theme";
import { ServerSideDatasource } from "./server_side_datasource";

interface ExportOptions {
  mode?: "all" | "rows";
  filter_model?: Record<string, unknown>;
  row_group_cols?: Array<{ id: string; field: string; displayName: string }>;
  selected_group_paths?: string[][];
  row_ids?: string[];
  search_text?: string;
}

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "searchInput",
    "confirmExportModal",
  ];

  static values = {
    ...AgGridBaseController.values,
    gridDataUrl:         String,
    cacheBlockSize:      { type: Number, default: 100 },
    searchText:          String,
    exportUrl:           String,
    advancedGridDataUrl: String,
    filterValuesUrl:     String,
  };

  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly hasSearchInputTarget: boolean;
  declare readonly confirmExportModalTarget: HTMLElement;
  declare readonly hasConfirmExportModalTarget: boolean;

  declare readonly gridDataUrlValue: string;
  declare readonly cacheBlockSizeValue: number;
  declare searchTextValue: string;
  declare readonly exportUrlValue: string;
  declare readonly advancedGridDataUrlValue: string;
  declare readonly filterValuesUrlValue: string;

  private sideBarConfig: false | SideBarDef = false;
  private pendingExportFormat: "csv" | "xlsx" | null = null;

  connect() {
    super.connect(); // license key + outside click handler

    if (!this.hasGridTarget) return;

    const hasAdvanced = this.advancedColumnDefsValue.length > 0;

    if (hasAdvanced && localStorage.getItem(this.modeStorageKey) === "advanced") {
      this.isBasicMode = false;
    }

    this.sideBarConfig = this.buildSideBarConfig(hasAdvanced) as false | SideBarDef;

    const initialDataUrl = this.isBasicMode ? this.gridDataUrlValue : this.advancedGridDataUrlValue;
    const datasource = new ServerSideDatasource(
      initialDataUrl,
      () => this.searchTextValue,
      (rows) => this.gridApi?.setGridOption("pinnedBottomRowData", rows)
    );

    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};

    const options: GridOptions = {
      theme: ebTheme,
      rowModelType: "serverSide",
      serverSideDatasource: datasource,
      cacheBlockSize: this.cacheBlockSizeValue,
      enableFilterHandlers: true,
      rowBuffer: 50,
      animateRows: false,
      rowHeight: 56,
      getChildCount: (data) => (data as Record<string, unknown>)?.childCount as number,
      getRowId: (params) => {
        if (params.data.id != null) return String(params.data.id);
        const groupKey = Object.entries(params.data as Record<string, unknown>)
          .map(([k, v]) => `${k}:${String(v)}`)
          .join("|");
        const parentPrefix = (params.parentKeys ?? []).join("/");
        return `grp_${params.level}_${parentPrefix}_${groupKey}`;
      },
      getContextMenuItems: (params) => {
        const items: (DefaultMenuItem | MenuItemDef)[] = (params.defaultItems ?? []).filter((item) => item !== "export");
        items.push({
          name: "Export",
          icon: '<span class="ag-icon ag-icon-save"></span>',
          subMenu: [
            { name: "CSV Export", icon: '<span class="ag-icon ag-icon-csv"></span>', action: () => this.exportCsv() },
            { name: "Excel Export", icon: '<span class="ag-icon ag-icon-excel"></span>', action: () => this.exportXlsx() },
          ],
        });
        return items;
      },
      getMainMenuItems: (params) =>
        params.column?.getColDef().enableRowGroup
          ? params.defaultItems
          : params.defaultItems.filter((item) => item !== "rowGroup"),
      onFirstDataRendered: () => {
        this.autoSizeNonFlexColumns();
      },
      onModelUpdated: () => {
        if (this.pendingAutoSize) {
          // Wait until actual row data is present — SSRM fires onModelUpdated
          // immediately on datasource reset (loading state) before blocks load.
          const firstRow = this.gridApi?.getDisplayedRowAtIndex(0);
          if (firstRow?.data !== undefined) {
            this.pendingAutoSize = false;
            this.autoSizeNonFlexColumns();
          }
        }
      },
      onColumnRowGroupChanged: () => {
        this.pendingAutoSize = true;
        this.updateHeaderCheckbox();
      },
      onFilterChanged: () => {
        this.updateHeaderCheckbox();
      },
      onCellSelectionChanged: (event: CellSelectionChangedEvent) => {
        if (!event.finished) return;
        const ranges = this.gridApi?.getCellRanges();
        if (!ranges?.length) return;
        for (const range of ranges) {
          if (!range.startRow || !range.endRow) continue;
          const hasCheckboxCol = range.columns.some((c) => c.getColId() === "ag-Grid-SelectionColumn");
          if (!hasCheckboxCol) continue;
          const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
          const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);
          const firstNode = this.gridApi?.getDisplayedRowAtIndex(start);
          const select = !firstNode?.isSelected();
          for (let i = start; i <= end; i++) {
            this.gridApi?.getDisplayedRowAtIndex(i)?.setSelected(select);
          }
        }
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.handleSelectionChanged(event);
      },
      onStateUpdated: (event: StateUpdatedEvent) => {
        const LAYOUT_SOURCES = new Set([
          "columnSizing", "columnOrder", "columnPinning", "columnVisibility",
          "columnGroup", "sort", "filter", "rowGroup", "pivot", "aggregation", "sideBar",
        ]);
        const AUTOSIZE_SOURCES = new Set(["columnVisibility", "columnPinning"]);
        if (event.sources.some((s) => LAYOUT_SOURCES.has(s))) {
          this.saveGridState();
        }
        if (event.sources.some((s) => AUTOSIZE_SOURCES.has(s))) {
          this.autoSizeNonFlexColumns();
        }
      },
      ...this.gridOptionsValue,
      columnDefs: this.applyFilterValueCallbacks(
        this.isBasicMode ? this.basicColDefs(this.columnDefsValue) : this.advancedColumnDefsValue
      ),
      defaultColDef: this.isBasicMode
        ? { ...baseColDef, suppressHeaderMenuButton: true }
        : { ...baseColDef },
      suppressDragLeaveHidesColumns: this.isBasicMode,
      autoGroupColumnDef: {
        minWidth: 220,
      },
      rowSelection: {
        mode: "multiRow",
        checkboxes: true,
        headerCheckbox: false,
        ...(this.gridOptionsValue.rowSelection as Record<string, unknown> ?? {}),
        isRowSelectable: this.rowSelectableCallback,
      },
      components: {
        statusCellRenderer: StatusCellRenderer,
        ...((this.gridOptionsValue.components as Record<string, unknown>) ?? {}),
      },
    };

    this.gridApi = createGrid(this.gridTarget, options);

    if (!this.isBasicMode) {
      this.setActiveToggle("advanced");
      if (this.sideBarConfig) this.gridApi.setGridOption("sideBar", this.sideBarConfig);
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.style.display = "";
      if (this.hasFilterToggleTarget) this.filterToggleTarget.style.display = "";
    }

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      const exportWrapper = this.hasExportToggleTarget
        ? this.exportToggleTarget.closest(".ag-grid-component-export-wrapper")
        : null;
      if (exportWrapper?.contains(target)) return;
      this.gridApi?.clearCellSelection();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);

    const isAutoHeight = this.gridOptionsValue?.domLayout === "autoHeight";
    if (!isAutoHeight) {
      this.initDynamicHeight();
    }

    this.loadGridState();
  }

  // disconnect() is fully handled by super — no SSRM-specific teardown needed

  onSearchChanged() {
    if (!this.gridApi || !this.hasSearchInputTarget) return;
    this.searchTextValue = this.searchInputTarget.value;
    this.gridApi.refreshServerSide({ purge: true });
    this.updateHeaderCheckbox();
  }

  switchToBasic() {
    if (!this.gridApi) return;
    this.isBasicMode = true;
    localStorage.setItem(this.modeStorageKey, "basic");
    this.setActiveToggle("basic");
    this.gridApi.closeToolPanel();
    this.gridApi.setGridOption("sideBar", false);
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "none";
      this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.style.display = "none";
      this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { ...baseColDef, suppressHeaderMenuButton: true });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", true);
    this.gridApi.clearCellSelection();
    this.resetGridState();
    this.reinitDatasource(this.gridDataUrlValue, this.basicColDefs(this.columnDefsValue));
  }

  switchToAdvanced() {
    if (!this.gridApi) return;
    this.isBasicMode = false;
    localStorage.setItem(this.modeStorageKey, "advanced");
    this.setActiveToggle("advanced");
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { ...baseColDef });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", false);
    this.gridApi.clearCellSelection();
    this.resetGridState();
    this.reinitDatasource(this.advancedGridDataUrlValue, this.advancedColumnDefsValue);
    if (this.sideBarConfig) {
      this.gridApi.setGridOption("sideBar", this.sideBarConfig);
    }
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.style.display = "";
    if (this.hasFilterToggleTarget) this.filterToggleTarget.style.display = "";
  }

  exportCsv() {
    this.closeExportDropdown();
    if (!this.gridApi) return;

    const ranges = this.gridApi.getCellRanges();
    if (ranges && this.isActiveRange(ranges)) {
      this.exportSelectedCellsAsCsv(ranges);
      return;
    }

    const selOptions = this.resolveSelectionExportOptions();
    if (selOptions) {
      void this.triggerAsyncExport("csv", selOptions);
      return;
    }

    this.pendingExportFormat = "csv";
    this.openExportConfirmModal();
  }

  exportXlsx() {
    this.closeExportDropdown();
    if (!this.gridApi) return;

    const ranges = this.gridApi.getCellRanges();
    if (ranges && this.isActiveRange(ranges)) {
      this.exportSelectedCellsAsXlsx(ranges);
      return;
    }

    const selOptions = this.resolveSelectionExportOptions();
    if (selOptions) {
      void this.triggerAsyncExport("xlsx", selOptions);
      return;
    }

    this.pendingExportFormat = "xlsx";
    this.openExportConfirmModal();
  }

  openExportConfirmModal() {
    if (!this.hasConfirmExportModalTarget) return;
    this.confirmExportModalTarget.removeAttribute("hidden");
    document.body.style.overflow = "hidden";
    const cancelBtn = this.confirmExportModalTarget.querySelector<HTMLButtonElement>(
      "[data-action*='cancelExport']"
    );
    if (cancelBtn) setTimeout(() => cancelBtn.focus(), 50);
  }

  closeExportConfirmModal() {
    if (!this.hasConfirmExportModalTarget) return;
    this.confirmExportModalTarget.setAttribute("hidden", "");
    document.body.style.overflow = "";
  }

  backdropClickExportModal(event: Event) {
    if (event.target === this.confirmExportModalTarget) this.closeExportConfirmModal();
  }

  confirmExport() {
    this.closeExportConfirmModal();
    const format = this.pendingExportFormat;
    if (!format || !this.gridApi) return;
    this.pendingExportFormat = null;

    void this.triggerAsyncExport(format, {
      mode: "all",
      filter_model: this.gridApi.getFilterModel() as Record<string, unknown>,
      row_group_cols: this.getRowGroupColDefs(),
      search_text: this.searchTextValue || undefined,
    });
  }

  cancelExport() {
    this.closeExportConfirmModal();
    this.pendingExportFormat = null;
  }

  private async triggerAsyncExport(format: "csv" | "xlsx", options: ExportOptions = {}) {
    if (!this.exportUrlValue) return;
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        },
        credentials: "same-origin",
        body: JSON.stringify({ format, ...options }),
      });
      if (response.ok) {
        const html = await response.text();
        if (html.trim()) window.Turbo?.renderStreamMessage(html);
      }
    } catch {
      // Export request failed silently — user can retry
    }
  }

  private reinitDatasource(gridDataUrl: string, columnDefs: ColDef[]) {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("columnDefs", this.applyFilterValueCallbacks(columnDefs));
    this.autoSizeNonFlexColumns();
    this.pendingAutoSize = true;
    const datasource = new ServerSideDatasource(
      gridDataUrl,
      () => this.searchTextValue,
      (rows) => this.gridApi?.setGridOption("pinnedBottomRowData", rows)
    );
    this.gridApi.setGridOption("serverSideDatasource", datasource);
  }

  private async loadGridState() {
    if (!this.gridStateUrlValue || !this.gridApi) return;
    try {
      const response = await fetch(this.gridStateUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return;
      const { state } = await response.json() as { state: GridState };
      if (!state || !this.gridApi) return;
      this.isRestoringState = true;
      this.gridApi.setState(state);
      requestAnimationFrame(() => {
        this.isRestoringState = false;
        this.syncPanelButtonStates();
        this.updateHeaderCheckbox();
      });
    } catch {
      // Non-critical — grid functions without saved state
    }
  }

  // Debounced 2 s — defined as an arrow property so `this` is bound and the
  // same reference can be cleared on disconnect if needed.
  private saveGridState = (() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return () => {
      if (!this.gridApi || !this.gridStateUrlValue || this.isRestoringState) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (!this.gridApi || !this.gridStateUrlValue) return;
        const state = this.gridApi.getState();
        const csrfToken =
          (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
        fetch(this.gridStateUrlValue, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "same-origin",
          body: JSON.stringify({ state }),
        }).catch(() => {
          // Non-critical — state save failure is silent
        });
      }, 2000);
    };
  })();

  private handleSelectionChanged(_event: SelectionChangedEvent) {
    if (!this.gridApi) return;

    // Check selection state first and handle the select-all case without it.
    const rawSel = this.gridApi.getServerSideSelectionState() as { selectAll?: boolean } | null;
    if (rawSel?.selectAll === true) {
      this.hasRowSelection = true;
      if (this.hasActionsMenuTarget) {
        this.actionsMenuTarget.classList.add("visible");
        if (this.hasSelectedCountTarget) {
          this.selectedCountTarget.textContent = "All selected";
        }
        this.clearActions();
      }
      return;
    }

    const selectedRows = this.gridApi.getSelectedRows() as Array<Record<string, unknown>>;
    const count = selectedRows.length;
    this.hasRowSelection = count > 0;

    if (!this.hasActionsMenuTarget) return;

    if (count > 0) {
      this.actionsMenuTarget.classList.add("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = `${count} selected`;
      }
      this.renderActions(selectedRows);
    } else {
      this.actionsMenuTarget.classList.remove("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = "";
      }
      this.clearActions();
    }
  }

  private isActiveRange(ranges: CellRange[]): boolean {
    if (!ranges || ranges.length === 0) return false;
    const hasExportableCols = ranges.some((r) =>
      r.columns.some((c) => c.getColId() !== "ag-Grid-SelectionColumn")
    );
    if (!hasExportableCols) return false;
    const range = ranges[0];
    const rowSpan = Math.abs((range.endRow?.rowIndex ?? 0) - (range.startRow?.rowIndex ?? 0)) + 1;
    return rowSpan > 1 || range.columns.length > 1;
  }

  private resolveRangeSelection(ranges: CellRange[]) {
    const displayedCols = this.gridApi!.getAllDisplayedColumns() ?? [];
    const rangeColIds = new Set<string>(
      ranges.reduce<string[]>((acc, r) => acc.concat(r.columns.map((c: Column) => c.getColId())), [])
    );
    const cols = displayedCols.filter(
      (c) => rangeColIds.has(c.getColId()) && c.getColId() !== "ag-Grid-SelectionColumn"
    );

    const rowIndices = new Set<number>();
    for (const range of ranges) {
      const start = Math.min(range.startRow!.rowIndex, range.endRow!.rowIndex);
      const end = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex);
      for (let i = start; i <= end; i++) rowIndices.add(i);
    }

    const sortedIndices = Array.from(rowIndices).sort((a, b) => a - b);
    return { cols, sortedIndices };
  }

  private exportSelectedCellsAsCsv(ranges: CellRange[]) {
    if (!this.gridApi) return;
    const { cols, sortedIndices } = this.resolveRangeSelection(ranges);

    const csvRows: string[][] = [];
    csvRows.push(cols.map((col) => col.getColDef().headerName ?? col.getColId()));

    for (const idx of sortedIndices) {
      const node = this.gridApi.getDisplayedRowAtIndex(idx);
      if (!node) continue;
      if (node.group) {
        const count = (node.data as Record<string, unknown>)?.childCount as number | undefined
          ?? node.allChildrenCount
          ?? 0;
        const label = `${node.key ?? ""} (${count})`;
        csvRows.push(cols.map((_, i) => (i === 0 ? label : "")));
        continue;
      }
      if (!node.data) continue;
      csvRows.push(cols.map((col) => {
        const field = col.getColDef().field;
        const val = field ? (node.data as Record<string, unknown>)[field] : undefined;
        return val != null ? String(val) : "";
      }));
    }

    const csv = "﻿" + csvRows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "export-selection.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  private exportSelectedCellsAsXlsx(ranges: CellRange[]) {
    if (!this.gridApi) return;
    const { cols, sortedIndices } = this.resolveRangeSelection(ranges);

    const allowedIds = new Set<string>(
      sortedIndices
        .map((idx) => this.gridApi!.getDisplayedRowAtIndex(idx)?.id)
        .filter((id): id is string => id !== undefined)
    );

    this.gridApi.exportDataAsExcel({
      columnKeys: cols,
      shouldRowBeSkipped: (params) => !allowedIds.has(params.node.id ?? ""),
      fileName: "export-selection.xlsx",
    });
  }

  private resetGridState() {
    if (!this.gridApi) return;
    this.gridApi.setFilterModel({});
    this.gridApi.applyColumnState({
      defaultState: { sort: null, rowGroup: false, rowGroupIndex: null, hide: false },
    });
  }

  private applyFilterValueCallbacks(columnDefs: ColDef[]): ColDef[] {
    if (!this.filterValuesUrlValue) return columnDefs;
    return columnDefs.map((col) => {
      if (col.filter !== "agSetColumnFilter") return col;
      if ((col.filterParams as Record<string, unknown>)?.values) return col;
      const colId = String(col.field ?? col.colId ?? "");
      return {
        ...col,
        filterParams: {
          ...((col.filterParams as Record<string, unknown>) ?? {}),
          values: (params: { success: (v: string[]) => void }) => {
            fetch(
              `${this.filterValuesUrlValue}?column=${encodeURIComponent(colId)}`,
              { credentials: "same-origin", headers: { Accept: "application/json" } }
            )
              .then((r) => r.json())
              .then((data: { values: string[] }) => params.success(data.values ?? []))
              .catch(() => params.success([]));
          },
        },
      };
    });
  }

  private resolveSelectionExportOptions(): ExportOptions | null {
    if (!this.gridApi) return null;

    // Check selectAll before calling getSelectedNodes() — calling getSelectedNodes()
    // triggers warning #199 when selectAll is active in SSRM.
    const rawSel = this.gridApi.getServerSideSelectionState() as { selectAll?: boolean } | null;
    if (rawSel?.selectAll === true) return null;

    const selectedNodes = this.gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return null;

    const leafIds = selectedNodes
      .filter((n) => !n.group && n.id != null)
      .map((n) => n.id as string);
    if (leafIds.length > 0) {
      return { mode: "rows", row_ids: leafIds };
    }

    const groupNodes = selectedNodes.filter((n) => n.group && n.key != null);
    if (groupNodes.length === 0) return null;

    // Build the full ancestor path for each selected group node (e.g. ["Not Voted", "Bat"]).
    // Then remove any path that is a prefix of a deeper path — this handles the case where
    // AG Grid auto-checks a parent group when a child group is selected.
    const allPaths = groupNodes.map((node) => this.buildGroupPath(node));
    const selectedGroupPaths = allPaths.filter(
      (path) => !allPaths.some(
        (other) => other.length > path.length && path.every((key, i) => other[i] === key)
      )
    );

    if (selectedGroupPaths.length === 0) return null;

    return {
      mode: "all",
      filter_model: this.gridApi.getFilterModel() as Record<string, unknown>,
      row_group_cols: this.getRowGroupColDefs(),
      selected_group_paths: selectedGroupPaths,
      search_text: this.searchTextValue || undefined,
    };
  }

  private buildGroupPath(node: IRowNode): string[] {
    const path: string[] = [];
    let current: IRowNode | null = node;
    while (current?.group && current.key != null) {
      path.unshift(String(current.key));
      current = current.parent ?? null;
    }
    return path;
  }

  private updateHeaderCheckbox() {
    if (!this.gridApi) return;
    const filterModel = this.gridApi.getFilterModel();
    const hasFilters = Object.keys(filterModel).length > 0;
    const hasGroups = (this.gridApi.getRowGroupColumns() ?? []).length > 0;
    const hasSearch = !!this.searchTextValue;
    const showHeader = hasFilters || hasGroups || hasSearch;

    const currentRowSel = (this.gridOptionsValue.rowSelection as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("rowSelection", {
      mode: "multiRow",
      checkboxes: true,
      ...currentRowSel,
      headerCheckbox: showHeader,
    });
  }

  private getRowGroupColDefs(): Array<{ id: string; field: string; displayName: string }> {
    if (!this.gridApi) return [];
    return (this.gridApi.getRowGroupColumns() ?? []).map((col) => ({
      id: col.getColId(),
      field: String(col.getColDef().field ?? col.getColId()),
      displayName: String(col.getColDef().headerName ?? col.getColId()),
    }));
  }

  private get modeStorageKey(): string {
    return `ssrm-mode:${this.gridStateUrlValue || location.pathname}`;
  }
}
